import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { api, diagnoseNoResponse } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { SocialAuthButtons } from '@/components/auth/SocialAuthButtons';
import toast from 'react-hot-toast';

interface FormData {
  name: string;
  email: string;
  password: string;
  acceptTerms: boolean;
}

export default function Register() {
  const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm<FormData>();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);

  const password = watch('password') ?? '';
  const acceptTerms = watch('acceptTerms');
  const [accountType, setAccountType] = useState<'BUYER' | 'SELLER'>('BUYER');
  const checks = {
    length: password.length >= 8,
    upper:  /[A-Z]/.test(password),
    lower:  /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    symbol: /[^A-Za-z0-9]/.test(password),
  };
  const score = Object.values(checks).filter(Boolean).length;
  const strengthLabel = ['Too short', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent'][score];
  const strengthColor = ['bg-bad', 'bg-bad', 'bg-warn', 'bg-warn', 'bg-ok', 'bg-ok'][score];

  async function onSubmit(values: FormData) {
    try {
      const { data } = await api.post('/auth/register', {
        name: values.name,
        email: values.email,
        password: values.password,
        accountType,
      });
      setSession(data.user, data.accessToken);
      // Send them to the dedicated verification step (not straight to the
      // dashboard). emailSent tells that page whether to prompt a resend.
      navigate('/verify-email', { state: { emailSent: data.emailSent } });
    } catch (e: any) {
      // No response at all - the API is down, crashed, or blocked by CORS.
      // Without this branch it fell through to the vague catch-all below,
      // which sent you hunting through the form for a fault that wasn't there.
      if (!e?.response) {
        return toast.error(await diagnoseNoResponse(), { duration: 9000 });
      }

      const { status, data } = e.response;

      if (status === 409) {
        return toast.error('That email is already registered. Try logging in instead.');
      }
      if (status === 429) {
        return toast.error('Too many sign-up attempts. Please wait a minute and try again.');
      }
      // 503 = database unavailable / schema out of date. The API explains it.
      if (status === 503 && data?.message) {
        return toast.error(data.message, { duration: 12000 });
      }
      // Zod rejections carry the offending field, e.g. "password: Needs a symbol".
      const issue = data?.issues?.[0];
      if (issue) {
        const field = Array.isArray(issue.path) ? issue.path.join('.') : '';
        return toast.error(field ? `${field}: ${issue.message}` : issue.message, { duration: 9000 });
      }
      if (data?.message && data.message !== 'Validation failed') {
        return toast.error(data.message, { duration: 9000 });
      }
      toast.error(`Could not register (error ${status}). Check the backend terminal for details.`, { duration: 9000 });
    }
  }

  return (
    <div className="relative min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center px-4 py-12 overflow-hidden">
      {/* Background - a curated closet of pre-loved fashion: what Souqli stands for */}
      <img
        src="https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=1920&h=1280&fit=crop&auto=format&q=80"
        alt="" aria-hidden
        onError={(e) => { (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1920&h=1280&fit=crop&auto=format&q=80'; }}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-ink/90 via-ink/74 to-ink/92" />
      <div className="absolute inset-0 mashrabiya opacity-[0.06] pointer-events-none" />

      <div className="relative z-10 w-full max-w-[440px]">

        {/* Brand + tagline + value props */}
        <div className="text-center mb-6">
          <Link to="/" className="display text-[44px] leading-none text-gold tracking-tight drop-shadow">Souqli</Link>
          <p className="text-cream/85 text-[15px] mt-3 leading-snug max-w-[32ch] mx-auto">
            Create an account. It's quick and free to browse verified pieces
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mt-4">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-cream/20 backdrop-blur px-3 py-1.5 text-[12px] text-cream">
              🛍️ Buy quality used items in under <span className="font-semibold text-gold">3&nbsp;min</span>
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-cream/20 backdrop-blur px-3 py-1.5 text-[12px] text-cream">
              🏷️ Sell your used items in under <span className="font-semibold text-gold">5&nbsp;min</span>
            </span>
          </div>
        </div>

        {/* Register card */}
        <div className="bg-white rounded-2xl shadow-frame border border-warmline p-5 sm:p-6">
          <form onSubmit={handleSubmit(onSubmit)}>

            {/* Account type */}
            <div className="grid grid-cols-2 gap-3">
              {([
                { v: 'BUYER',  icon: '🛍️', title: 'Buy', sub: 'Shop verified pre-loved' },
                { v: 'SELLER', icon: '🏷️', title: 'Sell', sub: 'List & get paid securely' },
              ] as const).map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setAccountType(opt.v)}
                  className={`text-left rounded-luxe border-2 p-3.5 transition ${
                    accountType === opt.v ? 'border-gold bg-gold/8' : 'border-warmline hover:border-gold/50'
                  }`}
                >
                  <div className="text-xl leading-none">{opt.icon}</div>
                  <div className="text-sm font-semibold mt-1.5">{opt.title}</div>
                  <div className="text-[11px] text-mute mt-0.5 leading-snug">{opt.sub}</div>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-mute mt-2 mb-4">
              {accountType === 'SELLER'
                ? 'You can browse and buy too - selling just unlocks listings & payouts.'
                : 'You can switch to selling later from your dashboard.'}
            </p>

            <div className="space-y-3">
              <div>
                <input className="input input-lg" autoComplete="name" placeholder="Full name"
                  {...register('name', { required: true, minLength: 2 })} />
                {errors.name && <p className="text-bad text-xs mt-1">Tell us your name.</p>}
              </div>

              <div>
                <input className="input input-lg" type="email" autoComplete="email" placeholder="Email address"
                  {...register('email', { required: true })} />
                {errors.email && <p className="text-bad text-xs mt-1">A valid email is required.</p>}
              </div>

              <div>
                <input
                  className="input input-lg"
                  type="password"
                  autoComplete="new-password"
                  placeholder="New password"
                  {...register('password', {
                    required: true,
                    validate: (v) =>
                      (v.length >= 8 && /[A-Z]/.test(v) && /[a-z]/.test(v) && /[0-9]/.test(v) && /[^A-Za-z0-9]/.test(v)) ||
                      'Use 8+ chars with upper & lowercase, a number and a symbol.',
                  })}
                />
                {password.length === 0 && (
                  <p className="text-[11px] text-mute mt-1.5">8+ characters with upper & lowercase, a number, and a symbol.</p>
                )}
                {errors.password && <p className="text-bad text-xs mt-1">{errors.password.message ?? 'Choose a stronger password.'}</p>}
                {password.length > 0 && (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-cream-deep rounded-full overflow-hidden">
                        <div className={`h-full ${strengthColor} transition-all`} style={{ width: `${(score / 5) * 100}%` }} />
                      </div>
                      <span className="stamp text-mute text-[9px]">{strengthLabel}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px]">
                      <PwBit ok={checks.length} label="8+ chars" />
                      <PwBit ok={checks.upper}  label="A-Z" />
                      <PwBit ok={checks.lower}  label="a-z" />
                      <PwBit ok={checks.number} label="0-9" />
                      <PwBit ok={checks.symbol} label="!@#$" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <label className="flex items-start gap-2.5 text-xs text-mute mt-4 leading-relaxed cursor-pointer">
              <input type="checkbox" className="mt-0.5 accent-gold" {...register('acceptTerms', { required: true })} />
              <span>
                I accept the Souqli <Link to="/terms" className="text-gold-dark hover:underline">Terms</Link> and{' '}
                <Link to="/privacy" className="text-gold-dark hover:underline">Privacy Policy</Link>.
              </span>
            </label>
            {errors.acceptTerms && <p className="text-bad text-xs mt-1">Please accept the terms to continue.</p>}

            <button
              disabled={isSubmitting || score < 5 || !acceptTerms}
              className="btn btn-lg btn-primary w-full mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Creating account…' : 'Create account'}
            </button>
            {!isSubmitting && score >= 5 && !acceptTerms && (
              <p className="text-[11px] text-mute text-center mt-2">Accept the terms to continue.</p>
            )}
          </form>

          <div className="flex items-center gap-3 text-mute my-4">
            <div className="flex-1 h-px bg-warmline" />
            <span className="stamp text-[9px]">or sign up with</span>
            <div className="flex-1 h-px bg-warmline" />
          </div>
          <SocialAuthButtons
            onAuthed={(u, token) => {
              setSession(u, token);
              navigate(u?.role === 'ADMIN' ? '/control-7f3a9b2c' : u?.emailVerified === false ? '/verify-email' : '/dashboard');
            }}
            onTwoFactor={() => {
              toast('You already have an account. Please log in to continue.');
              navigate('/login');
            }}
          />

          <div className="text-center mt-5 pt-4 border-t border-warmline">
            <Link to="/login" className="text-[14px] text-gold-dark font-medium hover:underline">Already have an account? Log in</Link>
          </div>
        </div>

        <p className="text-center text-[11px] text-cream/70 mt-5 leading-relaxed max-w-[38ch] mx-auto">
          Want the <span className="text-gold">Verified seller</span> badge later? Add ID from Dashboard → Profile after signup. It’s optional and never blocks buying.
        </p>
      </div>
    </div>
  );
}

function PwBit({ ok, label }: { ok: boolean; label: string }) {
  return <span className={ok ? 'text-ok' : 'text-mute'}>{ok ? '✓' : '○'} {label}</span>;
}
